import type { CreateClientFormData } from '@/types/clientsApiTypes';
import type { RelatedDataKeys } from '../../types';

export const getTravelPreferencesRelatedData = (
  key: RelatedDataKeys,
  fieldName: string,
  fieldValue: unknown,
  formValues: CreateClientFormData,
  fullPath: string
) => {
  const isPrimary = fullPath.startsWith('primary_passenger');
  const relatedMatch = fullPath.match(/^related_passengers\.(\d+)/);
  const relatedIndex = relatedMatch ? parseInt(relatedMatch[1], 10) : null;

  if (key === 'personal') {
    if (isPrimary) {
      return getPersonalRelatedDataPrimary(fieldName, fieldValue, formValues);
    } else if (relatedIndex !== null) {
      return getPersonalRelatedDataRelatedPassenger(fieldName, fieldValue, formValues, relatedIndex);
    }
  }

  switch (key) {
    case 'fly':
      return getFlyRelatedData(fieldName, fieldValue, formValues);
    case 'stay':
      return getStayRelatedData(fieldName, fieldValue, formValues);
    case 'general':
      return getGeneralRelatedData(fieldName, fieldValue, formValues);
    case 'travel':
      return getTravelRelatedData(fieldName, fieldValue);
    default:
      return {};
  }
};

const getPersonalRelatedDataPrimary = (
  fieldName: string,
  fieldValue: unknown,
  formValues: CreateClientFormData
) => {
  const sectionData =
    formValues?.primary_passenger?.preference_data?.personal && typeof formValues.primary_passenger.preference_data.personal === 'object'
      ? { ...formValues.primary_passenger.preference_data.personal }
      : {};

  const cleanedSectionData: Record<string, unknown> = Object.fromEntries(
    Object.entries(sectionData).filter(
      ([key]) =>
        key !== 'personal_metric_system' &&
        !key.startsWith('us') &&
        !key.startsWith('metric')
    )
  );

  if (
    fieldName !== 'personal_metric_system' &&
    !fieldName.startsWith('us') &&
    !fieldName.startsWith('metric')
  ) {
    cleanedSectionData[fieldName] = fieldValue;
  }

  return {
    preference_data: {
      personal: cleanedSectionData,
    }
  };
};

const getPersonalRelatedDataRelatedPassenger = (
  fieldName: string,
  fieldValue: unknown,
  formValues: CreateClientFormData,
  index: number
) => {
  const relatedPassenger = Array.isArray(formValues.related_passengers)
    ? formValues.related_passengers[index]
    : undefined;

  const sectionData =
    relatedPassenger?.preference_data?.personal && typeof relatedPassenger.preference_data.personal === 'object'
      ? { ...relatedPassenger.preference_data.personal }
      : {};

  const cleanedSectionData: Record<string, unknown> = Object.fromEntries(
    Object.entries(sectionData).filter(
      ([key]) =>
        key !== 'personal_metric_system' &&
        !key.startsWith('us') &&
        !key.startsWith('metric')
    )
  );

  if (
    fieldName !== 'personal_metric_system' &&
    !fieldName.startsWith('us') &&
    !fieldName.startsWith('metric')
  ) {
    cleanedSectionData[fieldName] = fieldValue;
  }

  return {
    preference_data: {
      personal: cleanedSectionData,
    }
  };
};

const getFlyRelatedData = (
  fieldName: string,
  fieldValue: unknown,
  formValues: CreateClientFormData
) => {
  const sectionData =
    formValues?.primary_passenger?.preference_data?.fly && typeof formValues.primary_passenger.preference_data.fly === 'object'
      ? formValues.primary_passenger.preference_data.fly
      : {};
  return {
    preference_data: {
      fly: {
        ...sectionData,
        [fieldName]: fieldValue,
      },
    },
  };
};

const getStayRelatedData = (
  fieldName: string,
  fieldValue: unknown,
  formValues: CreateClientFormData
) => {
  const sectionData =
    formValues?.primary_passenger?.preference_data?.stay && typeof formValues.primary_passenger.preference_data.stay === 'object'
      ? formValues.primary_passenger.preference_data.stay
      : {};
  return {
    preference_data: {
      stay: {
        ...sectionData,
        [fieldName]: fieldValue,
      },
    },
  };
};

const getGeneralRelatedData = (
  fieldName: string,
  fieldValue: unknown,
  formValues: CreateClientFormData
) => {
  const topLevelFields = [
    'general_interests',
    'beverage_preferences',
    'pillow_preferences',
    'room_configuration_preferences',
    'spa_preferences',
    'spa_preferences_special_requests',
    'likes',
    'dislikes'
  ];

  const existingPreferenceData =
    formValues?.primary_passenger?.preference_data && typeof formValues.primary_passenger.preference_data === 'object'
      ? formValues.primary_passenger.preference_data
      : {};

  if (topLevelFields.includes(fieldName)) {
    return {
      preference_data: {
        ...existingPreferenceData,
        [fieldName]: fieldValue,
      },
    };
  }

  return {
    preference_data: existingPreferenceData,
  };
};

const getTravelRelatedData = (
  fieldName: string,
  fieldValue: unknown,
) => {
  return {
    preference_data: {
      [fieldName]: fieldValue,
    },
  };
};